import { BULK_PDF_EXPORT_LIMIT, CreateCsvExportRequestDTO, PDF_SCOPE, PdfExportRecordType, PdfScope, resolveUsersRegisterUserType } from '@gearbelt/global';

import { ExportJob } from './export-tab';
import { buildPdfExportFilename, recordsSelection, REGISTER_SELECTION, registerAndRecordsSelection, singleRecordSelection } from './pdf-export-filename';

// Omit that distributes over a union so each member keeps its own discriminant. Plain
// `Omit<A | B, K>` collapses the union (keyof a union is only its shared keys), which
// would drop the `queryType` discriminant off CreateCsvExportRequestDTO.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

// The register query for one register, minus `filename`. Each register page builds this
// (reusing its Download CSV request spec) and the CSV path stamps the filename; the
// register-PDF path here stamps its own per-scope filename. Sharing the type keeps the
// per-register builders honest about the one field they must NOT set.
export type RegisterExportQuery<Q extends CreateCsvExportRequestDTO['queryType']> = DistributiveOmit<
  Extract<CreateCsvExportRequestDTO, { queryType: Q }>,
  'filename'
>;

// Active, non-select column ids in display order — the column spec every register's
// export request carries. Shared so a register page doesn't re-derive it inline.
export function activeExportColumnIds<T extends { id: string; active: boolean }>(columnOrder: readonly T[]): T['id'][] {
  return columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
}

// Active column ids restricted to those the exporter can actually produce. Registers
// whose table shows columns the CSV registry intentionally omits (schedule checks:
// Asset Location, Work Order) must filter to the registry's keys, else the omitted
// ids resolve to blank `-` columns in the export. `allowedIds` is the registry key set.
export function exportColumnIdsIn<T extends { id: string; active: boolean }>(columnOrder: readonly T[], allowedIds: ReadonlySet<string>): T['id'][] {
  return activeExportColumnIds(columnOrder).filter((id) => allowedIds.has(id));
}

// The filename register slug for a register request. Almost always the queryType,
// except the shared 'users' queryType backs two registers — Gearbelt Contributors
// and Sitemate Users — distinguished only by `filters.userType`. queryType narrows
// the discriminated union, so filters.userType is typed here without a cast.
function registerDisplaySlug(register: DistributiveOmit<CreateCsvExportRequestDTO, 'filename'>): string {
  if (register.queryType === 'users') {
    return resolveUsersRegisterUserType(register.filters?.userType);
  }
  return register.queryType;
}

type BuildPdfExportJobInput = {
  scope: PdfScope;
  // Only the record-carrying scopes ('records', 'register-and-records') need a record
  // type. Data registers export the 'register' scope alone and have no record type.
  recordType?: PdfExportRecordType;
  // The selected records (id + number). A single record is named by its reference,
  // so the record — not just its id — has to reach the filename builder. `repairId`
  // is read by the issues 'register-issues-and-repairs' scope and `scheduleFormId`
  // by the checks 'register-checks-and-form-records' scope, each interleaving a
  // parent record with its linked form. `issues` / `checks` (with their own child
  // ids) are read only by the 'register-work-orders-and-attached' scope, which
  // interleaves each work order with its attached records two levels deep.
  records: Array<{
    id: string;
    number?: number | null;
    repairId?: string | null;
    scheduleFormId?: string | null;
    issues?: Array<{ id: string; repairId?: string | null }> | null;
    checks?: Array<{ id: string; scheduleFormId?: string | null }> | null;
  }>;
  // The register query for the 'register' / 'register-and-records' scopes. Filename is
  // omitted by construction — buildPdfExportJob stamps the per-scope download name — so
  // callers can't pass a value that would be silently overwritten. Distributive so the
  // discriminated union (and its per-register queryType) survives the omit.
  register?: DistributiveOmit<CreateCsvExportRequestDTO, 'filename'>;
};

// Maps a menu scope selection to the export job the progress tab runs, stamping the
// download filename (Gearbelt naming standard) for every scope from one place so the
// three scopes can't drift. "records" runs on the bulk endpoint; "register" /
// "register-and-records" carry the pdfScope contract.
export function buildPdfExportJob({ scope, recordType, records, register }: BuildPdfExportJobInput): ExportJob {
  const recordIds = records.map((record) => record.id);
  const recordCount = records.length;
  switch (scope) {
    case 'records': {
      if (!recordType) throw new Error('buildPdfExportJob: the "records" scope requires a recordType');
      const selection = recordCount === 1 ? singleRecordSelection(recordType, records[0]) : recordsSelection(recordCount);
      return {
        kind: 'pdf-bulk',
        request: { recordType, recordIds, filename: buildPdfExportFilename(recordType, selection) },
      };
    }
    case 'register': {
      if (!register) throw new Error('buildPdfExportJob: the "register" scope requires a register request');
      return {
        kind: 'pdf-scoped',
        request: { pdfScope: 'register', register: { ...register, filename: buildPdfExportFilename(registerDisplaySlug(register), REGISTER_SELECTION) } },
      };
    }
    case 'register-and-records': {
      if (!register) throw new Error('buildPdfExportJob: the "register-and-records" scope requires a register request');
      if (!recordType) throw new Error('buildPdfExportJob: the "register-and-records" scope requires a recordType');
      return {
        kind: 'pdf-scoped',
        request: {
          pdfScope: 'register-and-records',
          register: { ...register, filename: buildPdfExportFilename(registerDisplaySlug(register), registerAndRecordsSelection(recordCount)) },
          recordType,
          recordIds,
        },
      };
    }
    case 'register-issues-and-repairs': {
      if (!register) throw new Error('buildPdfExportJob: the "register-issues-and-repairs" scope requires a register request');
      // Interleave each issue with its linked repair so the merged PDF reads
      // issue → its repair → next issue → its repair. Issues with no repair
      // contribute only themselves.
      const manifest = records.flatMap((record) =>
        record.repairId
          ? [
              { recordType: 'issue' as const, recordId: record.id },
              { recordType: 'repair' as const, recordId: record.repairId },
            ]
          : [{ recordType: 'issue' as const, recordId: record.id }],
      );
      return {
        kind: 'pdf-scoped',
        request: {
          pdfScope: 'register-issues-and-repairs',
          register: { ...register, filename: buildPdfExportFilename(register.queryType, registerAndRecordsSelection(recordCount)) },
          records: manifest,
        },
      };
    }
    case 'register-checks-and-form-records': {
      if (!register) throw new Error('buildPdfExportJob: the "register-checks-and-form-records" scope requires a register request');
      // Interleave each check with its linked schedule-form record so the merged
      // PDF reads check → its form → next check → its form. Checks with no
      // completed form record contribute only themselves.
      const manifest = records.flatMap((record) =>
        record.scheduleFormId
          ? [
              { recordType: 'check' as const, recordId: record.id },
              { recordType: 'schedule-form' as const, recordId: record.scheduleFormId },
            ]
          : [{ recordType: 'check' as const, recordId: record.id }],
      );
      return {
        kind: 'pdf-scoped',
        request: {
          pdfScope: 'register-checks-and-form-records',
          register: { ...register, filename: buildPdfExportFilename(register.queryType, registerAndRecordsSelection(recordCount)) },
          records: manifest,
        },
      };
    }
    case 'register-work-orders-and-attached': {
      if (!register) throw new Error('buildPdfExportJob: the "register-work-orders-and-attached" scope requires a register request');
      // Per work order, interleave its attached records two levels deep so the
      // merged PDF reads: work order → each issue (→ its repair) → each check (→
      // its schedule-form record). Records with no child contribute only themselves.
      const manifest = records.flatMap((record) => [
        { recordType: 'work-order' as const, recordId: record.id },
        ...(record.issues ?? []).flatMap((issue) =>
          issue.repairId
            ? [
                { recordType: 'issue' as const, recordId: issue.id },
                { recordType: 'repair' as const, recordId: issue.repairId },
              ]
            : [{ recordType: 'issue' as const, recordId: issue.id }],
        ),
        ...(record.checks ?? []).flatMap((check) =>
          check.scheduleFormId
            ? [
                { recordType: 'check' as const, recordId: check.id },
                { recordType: 'schedule-form' as const, recordId: check.scheduleFormId },
              ]
            : [{ recordType: 'check' as const, recordId: check.id }],
        ),
      ]);
      return {
        kind: 'pdf-scoped',
        request: {
          pdfScope: 'register-work-orders-and-attached',
          register: { ...register, filename: buildPdfExportFilename(registerDisplaySlug(register), registerAndRecordsSelection(recordCount)) },
          records: manifest,
        },
      };
    }
  }
}

// The compose scope (scope 4) that interleaves a parent record with its linked
// record — issues+repairs on the Issues register, checks+form-records on the
// two Schedule Check registers. Names the parent and the linked accessor so the
// over-limit guard below reads each register's records the same way.
type ComposeScopeConfig<T> = {
  // The scope 4 value this register dispatches (e.g. PDF_SCOPE.REGISTER_ISSUES_AND_REPAIRS).
  composeScope: PdfScope;
  // Plural parent noun for the compose-scope message (e.g. 'issues', 'checks').
  parentNoun: string;
  // Combined-record noun pair for the compose-scope message (e.g. 'issue and repair').
  composeRecordNoun: string;
  // Reads the linked record id off a selected parent — a linked record adds one
  // to that parent's export count. Returns null/undefined when there's no link.
  linkedRecordId: (record: T) => string | null | undefined;
};

// The shared "warn before the API rejects an over-limit set" guard every register
// page's Download-PDF menu runs. Returns a user-facing error message when the
// selection would exceed BULK_PDF_EXPORT_LIMIT for the chosen scope, or null when
// the export may proceed. Framework-agnostic (no toast) so the page owns display.
//
// The record-carrying scopes ('records' / 'register-and-records') export one
// record per parent, so the guard is a plain length check. The compose scope
// (scope 4) exports each parent plus its linked record, so its count is
// parents + links combined.
export function overLimitExportError<T>(scope: PdfScope, records: readonly T[], config: ComposeScopeConfig<T>): string | null {
  if ((scope === PDF_SCOPE.RECORDS || scope === PDF_SCOPE.REGISTER_AND_RECORDS) && records.length > BULK_PDF_EXPORT_LIMIT) {
    return `You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`;
  }
  if (scope === config.composeScope) {
    const recordCount = records.reduce((count, record) => count + (config.linkedRecordId(record) ? 2 : 1), 0);
    if (recordCount > BULK_PDF_EXPORT_LIMIT) {
      return `Select fewer ${config.parentNoun} — this scope exports up to ${BULK_PDF_EXPORT_LIMIT} combined ${config.composeRecordNoun} records.`;
    }
  }
  return null;
}
